// EnergyConsumptionReportPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Thermometer, Calendar, X, Download, Loader2, AlertCircle,
    CheckSquare, Square, ArrowLeft, Settings, Home, FileText, Search
} from 'lucide-react';
import { API_URL } from './archiveUtils';

// ==========================================
// Форматирование чисел (точка → запятая, русская локаль)
// ==========================================
const fmtRu = (val, digits) => {
    if (val == null || isNaN(val)) return '—';
    return Number(val).toFixed(digits).replace('.', ',');
};

// Для столбцов времени: целое → "64", дробное → "64,10"
const fmtTime = (val) => {
    if (val == null || isNaN(val)) return '—';
    const str = Number(val).toFixed(2);
    if (str.endsWith('.00')) return str.slice(0, -3);
    return str.replace('.', ',');
};

// ==========================================
// Модальное окно настроек
// ==========================================
const EnergyConfigModal = ({ isOpen, onClose, onGenerate }) => {
    const [addresses, setAddresses] = useState([]);
    const [groups, setGroups] = useState([]); // Добавляем группы
    const [selectedAddresses, setSelectedAddresses] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [dateFrom, setDateFrom] = useState(() => {
        const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0];
    });
    const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) loadAddresses();
    }, [isOpen]);

    const loadAddresses = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/api/reports/energy/addresses`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            // API теперь возвращает объект с двумя массивами
            setAddresses(response.data.addresses || []);
            setGroups(response.data.groups || []);
            setError(null);
        } catch (err) {
            setError(`Не удалось загрузить данные: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleGroupSelect = (groupId) => {
        if (!groupId) {
            setSelectedAddresses([]);
            return;
        }

        const group = groups.find(g => g.id === parseInt(groupId));
        if (!group) return;

        const groupDeviceNames = new Set(group.deviceNames);

        // Автоматически проставляем галочки на всех адресах, где есть устройства из группы
        const newSelected = addresses
            .map(addr => {
                const matchedDevices = addr.devices.filter(d => groupDeviceNames.has(d.name));
                if (matchedDevices.length > 0) {
                    return {
                        street: addr.street,
                        houseNumber: addr.houseNumber,
                        deviceNames: matchedDevices.map(d => d.name)
                    };
                }
                return null;
            })
            .filter(Boolean);

        setSelectedAddresses(newSelected);
    };

    const toggleSelectAll = () => {
        if (selectedAddresses.length === addresses.length && addresses.length > 0) {
            setSelectedAddresses([]);
        } else {
            setSelectedAddresses(addresses.map(addr => ({
                street: addr.street,
                houseNumber: addr.houseNumber,
                deviceNames: addr.devices.map(d => d.name)
            })));
        }
    };

    const toggleAddress = (addr) => {
        const key = `${addr.street}|${addr.houseNumber}`;
        setSelectedAddresses(prev => {
            const exists = prev.some(a => `${a.street}|${a.houseNumber}` === key);
            if (exists) return prev.filter(a => `${a.street}|${a.houseNumber}` !== key);
            return [...prev, { street: addr.street, houseNumber: addr.houseNumber, deviceNames: addr.devices.map(d => d.name) }];
        });
    };

    const handleSubmit = async () => {
        if (selectedAddresses.length === 0) {
            setError('Выберите хотя бы один адрес');
            return;
        }
        setIsLoading(true);
        try {
            await onGenerate({ dateFrom, dateTo, selectedAddresses });
            onClose();
        } catch (err) {
            setError('Ошибка формирования отчета');
        } finally {
            setIsLoading(false);
        }
    };

    // Фильтрация адресов и устройств по поисковому запросу
    const filteredAddresses = addresses.filter(addr => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();

        // Проверяем совпадения в адресе
        const addressStr = `${addr.street || ''} ${addr.houseNumber || ''} ${addr.formattedAddress || ''}`.toLowerCase();
        if (addressStr.includes(query)) return true;

        // Проверяем совпадения в названиях и описаниях устройств
        return addr.devices.some(d =>
            (d.name || '').toLowerCase().includes(query) ||
            (d.description || '').toLowerCase().includes(query)
        );
    });

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-slate-800 rounded-2xl border border-slate-700 w-full max-w-4xl flex flex-col shadow-2xl max-h-[90vh]">
                <div className="flex items-center justify-between p-4 border-b border-slate-700">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <Thermometer className="w-5 h-5 text-orange-400" /> Параметры отчета
                    </h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-700">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-5 custom-scrollbar">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-slate-400 text-xs mb-1.5">С даты</label>
                            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-orange-500" />
                        </div>
                        <div>
                            <label className="block text-slate-400 text-xs mb-1.5">По дату</label>
                            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-orange-500" />
                        </div>
                    </div>

                    <div>
                        <label className="block text-slate-400 text-xs mb-1.5">Быстрый выбор по группе</label>
                        <select
                            onChange={(e) => handleGroupSelect(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-orange-500"
                        >
                            <option value="">-- Выберите группу --</option>
                            {groups.map(g => (
                                <option key={g.id} value={g.id}>
                                    {g.name} ({g.deviceNames.length} уст.)
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-slate-400 text-xs mb-1.5">Адреса и устройства</label>

                        {/* Поле поиска */}
                        <div className="relative mb-2">
                            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Поиск по адресу или названию устройства..."
                                className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
                            />
                        </div>

                        <div className="bg-slate-900 border border-slate-700 rounded-lg overflow-hidden">
                            <div className="sticky top-0 z-10 bg-slate-800 border-b border-slate-700 p-2 flex items-center justify-between">
                                <button onClick={toggleSelectAll} className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition text-left flex-1">
                                    {selectedAddresses.length === addresses.length && addresses.length > 0
                                        ? <CheckSquare className="w-5 h-5" />
                                        : <Square className="w-5 h-5" />}
                                    <span className="text-white text-sm font-medium">Выбрать все ({addresses.length})</span>
                                </button>
                                <span className="text-xs font-medium text-orange-300 bg-orange-500/10 px-2.5 py-1 rounded-full border border-orange-500/20 ml-2 whitespace-nowrap">
                                    Выбрано: {selectedAddresses.length}
                                </span>
                            </div>
                            <div className="max-h-80 overflow-y-auto custom-scrollbar">
                                {filteredAddresses.length === 0 && !isLoading && (
                                    <div className="p-4 text-center text-slate-500">
                                        {addresses.length === 0 ? 'Нет доступных адресов' : 'Ничего не найдено'}
                                    </div>
                                )}
                                {filteredAddresses.map((addr, idx) => {
                                    const key = `${addr.street}|${addr.houseNumber}`;
                                    const isChecked = selectedAddresses.some(a => `${a.street}|${a.houseNumber}` === key);
                                    return (
                                        <label key={idx} className="flex items-start gap-3 p-3 hover:bg-slate-800 cursor-pointer transition border-b border-slate-700/50">
                                            <input type="checkbox" checked={isChecked} onChange={() => toggleAddress(addr)}
                                                className="mt-1 w-4 h-4 rounded border-slate-500 text-orange-600 focus:ring-orange-500 bg-slate-700" />
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <Home className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                                    <span className="text-white text-sm font-medium truncate">{addr.formattedAddress}</span>
                                                </div>
                                                <div className="text-slate-400 text-xs mt-1 truncate">
                                                    {addr.devices.map(d => d.name).join(', ')}
                                                </div>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {error && (
                        <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                            <span>{error}</span>
                        </div>
                    )}
                </div>

                <div className="p-4 border-t border-slate-700 flex justify-end gap-3 bg-slate-800 rounded-b-2xl">
                    <button onClick={onClose} className="px-4 py-2 text-slate-300 hover:text-white hover:bg-slate-700 rounded-lg text-sm font-medium transition">
                        Отмена
                    </button>
                    <button onClick={handleSubmit} disabled={isLoading || selectedAddresses.length === 0}
                        className="px-6 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg text-sm font-medium transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
                        Сформировать
                    </button>
                </div>
            </div>
        </div>
    );
};

// ==========================================
// Компонент одного блока дома — экранная версия
// ==========================================
const HouseReportBlock = ({ house, reportData, pageIndex }) => {
    return (
        <div className="bg-white text-slate-900 rounded-none shadow-lg print:shadow-none print:page-break-after mb-0" style={{ fontFamily: "'Times New Roman', Times, serif" }}>
            {/* Заголовок дома */}
            <div className="mb-2 pb-2 border-b border-slate-400">
                <div className="text-sm space-y-0.5 leading-tight">
                    <p><span className="font-semibold">Адрес МКД:</span> {house.fullAddressWithZone}</p>
                    <p><span className="font-semibold">Наименования УУ (дата ввода в экс.):</span> {house.allDeviceNamesWithDates}</p>
                    <p><span className="font-semibold">Площадь жилых помещений:</span> {fmtRu(house.houseAreaLive, 1)} м²</p>
                    <p><span className="font-semibold">Площадь нежилых помещений:</span> {fmtRu(house.houseAreaNotLive, 1)} м²</p>
                    <p className="text-xs text-slate-700 mt-1 font-mono bg-slate-100 p-1.5 rounded border border-slate-300 break-all">
                        {house.formula}
                    </p>
                </div>
            </div>

            {/* Таблица */}
            <div className="overflow-x-auto">
                <table className="w-full border-collapse" style={{ fontSize: '10px', fontFamily: "'Times New Roman', Times, serif" }}>
                    <thead className="bg-slate-100 text-slate-800 font-semibold">
                        <tr>
                            <th rowSpan="2" className="border border-slate-500 p-1 text-center w-20">Период</th>
                            <th rowSpan="2" className="border border-slate-500 p-1 text-center w-24">Имя УУ</th>
                            <th rowSpan="2" className="border border-slate-500 p-1 text-center">ВР, ч</th>
                            <th rowSpan="2" className="border border-slate-500 p-1 text-center">ВН, ч</th>
                            <th rowSpan="2" className="border border-slate-500 p-1 text-center">ВП, ч</th>
                            <th rowSpan="2" className="border border-slate-500 p-1 text-center">Вост, ч</th>
                            <th rowSpan="2" className="border border-slate-500 p-1 text-center">t факт, °C</th>
                            <th colSpan="3" className="border border-slate-500 p-1 text-center bg-blue-100/60">Объем, м³</th>
                            <th colSpan="4" className="border border-slate-500 p-1 text-center bg-orange-100/60">Энергия, Гкал</th>
                        </tr>
                        <tr className="bg-slate-50">
                            <th className="border border-slate-500 px-1 py-0.5 text-center">dVизм</th>
                            <th className="border border-slate-500 px-1 py-0.5 text-center">dVкорр</th>
                            <th className="border border-slate-500 px-1 py-0.5 text-center">dVсумм</th>
                            <th className="border border-slate-500 px-1 py-0.5 text-center">Qиз</th>
                            <th className="border border-slate-500 px-1 py-0.5 text-center">Qтп</th>
                            <th className="border border-slate-500 px-1 py-0.5 text-center">Qкорр</th>
                            <th className="border border-slate-500 px-1 py-0.5 text-center bg-orange-200/60">Qобщ</th>
                        </tr>
                    </thead>
                    <tbody>
                        {house.devices.map((dev, dIdx) => (
                            <tr key={dIdx} className="hover:bg-slate-50">
                                <td className="border border-slate-500 p-1 text-center whitespace-nowrap">{reportData.periodString}</td>
                                <td className="border border-slate-500 p-1 text-center whitespace-nowrap">{dev.name}</td>
                                <td className="border border-slate-500 p-1 text-center">{dev.timeVR}</td>
                                <td className="border border-slate-500 p-1 text-right">{fmtTime(dev.timeVN)}</td>
                                <td className="border border-slate-500 p-1 text-right">{fmtTime(dev.timeVP)}</td>
                                <td className="border border-slate-500 p-1 text-right">{fmtTime(dev.timeVO)}</td>
                                <td className="border border-slate-500 p-1 text-center">{fmtRu(reportData.tfact, 1)}</td>
                                <td className="border border-slate-500 p-1 text-right bg-blue-50/50">{fmtRu(dev.dIz, 2)}</td>
                                <td className="border border-slate-500 p-1 text-right bg-blue-50/50">{fmtRu(dev.dKorr, 2)}</td>
                                <td className="border border-slate-500 p-1 text-right bg-blue-50/50 font-medium">{fmtRu(dev.dSumm, 2)}</td>
                                <td className="border border-slate-500 p-1 text-right bg-orange-50/50">{fmtRu(dev.qIz, 2)}</td>
                                <td className="border border-slate-500 p-1 text-right bg-orange-50/50">{fmtRu(dev.qTp, 3)}</td>
                                <td className="border border-slate-500 p-1 text-right bg-orange-50/50">{fmtRu(dev.qKorr, 2)}</td>
                                <td className="border border-slate-500 p-1 text-right bg-orange-100/60 font-bold">{fmtRu(dev.qAllFact, 2)}</td>
                            </tr>
                        ))}
                        {/* Строка Итого */}
                        {house.devices.length > 1 && (
                            <tr className="bg-slate-100 font-bold text-slate-800">
                                <td colSpan="7" className="border border-slate-500 p-1 text-right">Итого:</td>
                                <td className="border border-slate-500 p-1 text-right bg-blue-100/50">{fmtRu(house.totalDIz, 2)}</td>
                                <td className="border border-slate-500 p-1 text-right bg-blue-100/50">{fmtRu(house.totalDKorr, 2)}</td>
                                <td className="border border-slate-500 p-1 text-right bg-blue-100/50">{fmtRu(house.totalDSumm, 2)}</td>
                                <td className="border border-slate-500 p-1 text-right bg-orange-100/50">{fmtRu(house.totalQIz, 2)}</td>
                                <td className="border border-slate-500 p-1 text-right bg-orange-100/50">{fmtRu(house.totalQTp, 3)}</td>
                                <td className="border border-slate-500 p-1 text-right bg-orange-100/50">{fmtRu(house.totalQKorr, 2)}</td>
                                <td className="border border-slate-500 p-1 text-right bg-orange-200 font-bold">{fmtRu(house.totalQAllFact, 2)}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ==========================================
// Главная страница отчета
// ==========================================
const EnergyConsumptionReportPage = () => {
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = useState(true);
    const [reportData, setReportData] = useState(null);
    const [lastParams, setLastParams] = useState(null);

    const handleGenerateReport = async (params) => {
        const token = localStorage.getItem('token');
        const response = await axios.post(`${API_URL}/api/reports/energy/generate`, params, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        setReportData(response.data);
        setLastParams(params);
    };

    // ==========================================
    // Общее тело документа (для Excel и Word)
    // ==========================================
    const buildReportBody = () => {
        let body = '';

        // Шапка организации
        body += `<div class="company-header">
            <p class="company-title">Общество с ограниченной ответственностью</p>
            <p class="company-title">«Управляющая компания Златоград»</p>
            <p class="company-title">(ООО «УК Златоград»)</p>
            <p class="company-info">Россия, 456209, г. Златоуст, ул. Шоссейная, 1, помещение № 17</p>
            <p class="company-info">ИНН 7404074032 КПП 740401001</p>
            <p class="company-line">____________________________________________________________________________________________________________</p>
        </div>`;

        // Блок согласований
        body += `<table class="no-border approvals-table">
            <tr>
                <td class="no-border" style="width: 33%; vertical-align: top; text-align: left;">
                    <b>Согласовано</b><br/>
                    Директор<br/>
                    ООО «НПО Автоматика-Сервис»<br/>
                    <span class="signature-line">_______________Кайсарова Т. В.</span><br/>
                    «&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;» _______________ 202_ г.
                </td>
                <td class="no-border" style="width: 34%; vertical-align: top; text-align: left;">
                    Согласовано<br/>
                    Главный энергетик-начальник<br/>
                    отдела № 263 АО «Златмаш»<br/>
                    <span class="signature-line">______________Тюрин С. А.</span><br/>
                    «&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;» ____________ 202_ г.
                </td>
                <td class="no-border" style="width: 33%; vertical-align: top; text-align: left;">
                    Согласовано<br/>
                    Директор<br/>
                    ООО УК «Златоград»<br/>
                    <span class="signature-line">__________ Сергеев В. Ю.</span><br/>
                    «&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;» ____________ 202_ г.
                </td>
            </tr>
        </table>`;

        // Заголовок документа
        body += `<div class="doc-title">Количество потребленной тепловой энергии</div>`;
        body += `<div class="doc-title" style="margin-top: 0;">за период ${reportData.periodStringFull}</div>`;
        body += `<div class="doc-subtitle">Отчет от ${reportData.dateOfReport}</div>`;

        // Блок формул
        body += `<div class="formula-block">`;
        body += `<p><b>Поставщик ТЭ:</b> АО "Златмаш"</p>`;
        body += `<p class="formula-title"><b>Формула расчета фактического количества ТЭ жилых и нежилых помещений, Гкал:</b></p>`;
        body += `<p class="formula-text"><b>Qобщ факт = Qтп + Qкорр. + Qиз,</b></p>`;
        body += `<p class="formula-desc">где Qтп - количество ТЭ, израсходованной на компенсацию потерь, Гкал,</p>`;
        body += `<p class="formula-desc indent">Qкорр. - количество ТЭ, израсходованной потребителем за время действия НС, Гкал,</p>`;
        body += `<p class="formula-desc indent">Qиз - рассчитанное теплосчетчиком в штатном режиме количество ТЭ, Гкал.</p>`;
        body += `<p class="formula-title"><b>Формула расчета количества ТЭ, израсходованного потребителем за время действия НС, Гкал:</b></p>`;
        body += `<p class="formula-text"><b>Qкорр. = f((Qиз / ВН) * ВП, Вост),</b></p>`;
        body += `<p class="formula-desc">где ВН - время наработки, ч,</p>`;
        body += `<p class="formula-desc indent">ВП - время простоя, ч,</p>`;
        body += `<p class="formula-desc indent">Вост - время отключения ТЭЦ (план), ч.</p>`;
        body += `</div>`;

        // Блоки по домам
        reportData.houses.forEach((house, idx) => {
            if (idx > 0) body += `<div class="page-break"></div>`;

            body += `<div class="house-block">`;
            body += `<div class="house-header">`;
            body += `<p><b>Адрес МКД:</b> ${house.fullAddressWithZone}</p>`;
            body += `<p><b>Наименования УУ (дата ввода в экс.):</b> ${house.allDeviceNamesWithDates}</p>`;
            body += `<p><b>Площадь жилых помещений:</b> ${fmtRu(house.houseAreaLive, 1)} м²</p>`;
            body += `<p><b>Площадь нежилых помещений:</b> ${fmtRu(house.houseAreaNotLive, 1)} м²</p>`;
            body += `<p class="house-formula">${house.formula}</p>`;
            body += `</div>`;

            body += `<table class="data-table">
                <thead>
                    <tr>
                        <th rowspan="2">Период</th>
                        <th rowspan="2">Имя УУ</th>
                        <th rowspan="2">ВР, ч</th>
                        <th rowspan="2">ВН, ч</th>
                        <th rowspan="2">ВП, ч</th>
                        <th rowspan="2">Вост, ч</th>
                        <th rowspan="2">t факт, °C</th>
                        <th colspan="3" class="vol-header">Объем, м³</th>
                        <th colspan="4" class="energy-header">Энергия, Гкал</th>
                    </tr>
                    <tr>
                        <th>dVизм</th><th>dVкорр</th><th>dVсумм</th>
                        <th>Qиз</th><th>Qтп</th><th>Qкорр</th><th>Qобщ</th>
                    </tr>
                </thead>
                <tbody>`;

            house.devices.forEach(dev => {
                body += `<tr>
                    <td class="tc">${reportData.periodString}</td>
                    <td class="tc">${dev.name}</td>
                    <td class="tc">${dev.timeVR}</td>
                    <td class="tr">${fmtTime(dev.timeVN)}</td>
                    <td class="tr">${fmtTime(dev.timeVP)}</td>
                    <td class="tr">${fmtTime(dev.timeVO)}</td>
                    <td class="tc">${fmtRu(reportData.tfact, 1)}</td>
                    <td class="tr bg-blue">${fmtRu(dev.dIz, 2)}</td>
                    <td class="tr bg-blue">${fmtRu(dev.dKorr, 2)}</td>
                    <td class="tr bg-blue">${fmtRu(dev.dSumm, 2)}</td>
                    <td class="tr bg-orange">${fmtRu(dev.qIz, 2)}</td>
                    <td class="tr bg-orange">${fmtRu(dev.qTp, 3)}</td>
                    <td class="tr bg-orange">${fmtRu(dev.qKorr, 2)}</td>
                    <td class="tr bg-orange-bold"><b>${fmtRu(dev.qAllFact, 2)}</b></td>
                </tr>`;
            });

            if (house.devices.length > 1) {
                body += `<tr class="total-row">
                    <td colspan="7" class="tr"><b>Итого:</b></td>
                    <td class="tr"><b>${fmtRu(house.totalDIz, 2)}</b></td>
                    <td class="tr"><b>${fmtRu(house.totalDKorr, 2)}</b></td>
                    <td class="tr"><b>${fmtRu(house.totalDSumm, 2)}</b></td>
                    <td class="tr"><b>${fmtRu(house.totalQIz, 2)}</b></td>
                    <td class="tr"><b>${fmtRu(house.totalQTp, 3)}</b></td>
                    <td class="tr"><b>${fmtRu(house.totalQKorr, 2)}</b></td>
                    <td class="tr"><b>${fmtRu(house.totalQAllFact, 2)}</b></td>
                </tr>`;
            }

            body += `</tbody></table>`;
            body += `</div>`;
        });

        return body;
    };

    // ==========================================
    // Полный HTML-документ (Excel или Word)
    // ==========================================
    const buildReportHtml = (isWord) => {
        const body = buildReportBody();

        if (isWord) {
            const wordStyles = `
                body {
                    font-family: 'Times New Roman', Times, serif;
                    font-size: 11pt;
                    line-height: 1.15;
                    margin: 0;
                    padding: 0;
                    color: #000;
                }
                p { margin: 0 0 2pt 0; padding: 0; }

                .company-header { text-align: center; margin-bottom: 16pt; padding: 0; }
                .company-title { font-weight: bold; font-size: 16pt; line-height: 1.2; margin: 0; }
                .company-info { font-weight: normal; font-size: 12pt; margin: 2pt 0; }
                .company-line { font-size: 12pt; margin-top: 8pt; letter-spacing: -1pt; }

                .approvals-table { width: 100%; margin-bottom: 20px; border-collapse: collapse; font-size: 12pt; line-height: 1.3; }
                .no-border, .no-border td, .no-border th { border: none !important; padding: 0 10pt; vertical-align: top; text-align: left; }
                .signature-line { margin-top: 8pt; display: inline-block; }

                .doc-title { text-align: center; font-weight: bold; font-size: 12pt; margin: 14pt 0 1pt 0; }
                .doc-subtitle { text-align: center; font-size: 10pt; margin-bottom: 10pt; }

                .formula-block { font-size: 11pt; line-height: 1.3; margin: 8pt 0 12pt 0; }
                .formula-title { margin-top: 4pt; text-indent: 0; }
                .formula-text { text-indent: 0; }
                .formula-desc { margin: 0; }
                .formula-desc.indent { padding-left: 1.6em; }

                .house-block { padding: 0 0.7cm 0.5cm 0.7cm; margin-top: 12pt; }
                .house-header { font-size: 11pt; line-height: 1.3; margin-bottom: 6pt; border-bottom: 1px solid #94a3b8; padding-bottom: 4pt; }
                .house-header p { margin: 1pt 0; }
                .house-formula {
                    font-family: 'Times New Roman', Times, serif;
                    font-size: 11pt;
                    padding: 4pt;
                    margin-top: 4pt;
                    word-wrap: break-word;
                }

                .data-table { border-collapse: collapse; width: 100%; font-size: 9pt; margin-top: 4pt; }
                .data-table th, .data-table td { border: 1px solid #64748b; padding: 2pt 3pt; word-wrap: break-word; overflow-wrap: break-word; vertical-align: middle; }
                .data-table th { font-weight: bold; text-align: center; font-size: 9pt; background: #f1f5f9; }
                .data-table .tc { text-align: center; }
                .data-table .tr { text-align: right; }
                .data-table .bg-blue { background: #eff6ff; }
                .data-table .bg-orange { background: #fff7ed; }
                .data-table .bg-orange-bold { background: #ffedd5; }
                .data-table .vol-header { background: #dbeafe; }
                .data-table .energy-header { background: #ffedd5; }
                .data-table .total-row { background: #f1f5f9; font-weight: bold; }

                .page-break { page-break-before: always; }
            `;

            return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
            <head>
                <meta charset="utf-8">
                <title>Отчет по тепловой энергии</title>
                <!--[if gte mso 9]>
                <xml>
                    <w:WordDocument>
                        <w:View>Print</w:View>
                        <w:Zoom>100</w:Zoom>
                        <w:SpellingState>Clean</w:SpellingState>
                        <w:GrammarState>Clean</w:GrammarState>
                        <w:DoNotOptimizeForBrowser/>
                    </w:WordDocument>
                </xml>
                <![endif]-->
                <style>
                    @page WordSection1 {
                        size: 21cm 29.7cm;
                        mso-page-orientation: portrait;
                        margin: 0.5cm 0.5cm 0.5cm 0.5cm;
                        mso-header-margin: 0cm;
                        mso-footer-margin: 0cm;
                    }
                    div.WordSection1 { page: WordSection1; }
                    ${wordStyles}
                </style>
            </head>
            <body>
                <div class="WordSection1">
                    ${body}
                </div>
            </body>
            </html>`;
        }

        // Excel — альбомная ориентация
        const excelStyles = `
            body { font-family: Arial, sans-serif; font-size: 10pt; }
            p { margin: 0 0 2px 0; padding: 0; }
            
            .company-header { text-align: center; margin-bottom: 15px; }
            .company-title { font-weight: bold; font-size: 14pt; line-height: 1.2; margin: 0; }
            .company-info { font-weight: normal; font-size: 11pt; margin: 2px 0; }
            .company-line { font-size: 11pt; margin-top: 8px; letter-spacing: -1px; }

            .approvals-table { width: 100%; margin-bottom: 20px; border-collapse: collapse; font-size: 11pt; line-height: 1.3; }
            .no-border, .no-border td, .no-border th { border: none !important; padding: 0 10px; vertical-align: top; text-align: left; }
            .signature-line { margin-top: 8px; display: inline-block; }

            .doc-title { text-align: center; font-weight: bold; font-size: 12pt; margin: 14px 0 1px 0; }
            .doc-subtitle { text-align: center; font-size: 10pt; margin-bottom: 10px; }

            .formula-block { font-size: 10pt; line-height: 1.3; margin: 8px 0 12px 0; }
            .formula-title { margin-top: 4px; text-indent: 0; }
            .formula-text { text-indent: 0; }
            .formula-desc { margin: 0; }
            .formula-desc.indent { padding-left: 1.6em; }

            .house-block { padding: 0; margin-top: 10px; }
            .house-header { font-size: 10pt; line-height: 1.3; margin-bottom: 6px; border-bottom: 1px solid #94a3b8; padding-bottom: 4px; }
            .house-header p { margin: 1px 0; }
            .house-formula {
                font-family: 'Courier New', monospace;
                font-size: 8pt;
                background: #f1f5f9;
                border: 1px solid #cbd5e1;
                padding: 4px;
                margin-top: 4px;
                word-wrap: break-word;
            }

            .data-table { border-collapse: collapse; width: 100%; font-size: 9pt; margin-top: 4px; }
            .data-table th, .data-table td { border: 1px solid #64748b; padding: 3px; word-wrap: break-word; overflow-wrap: break-word; vertical-align: middle; }
            .data-table th { font-weight: bold; text-align: center; font-size: 8pt; background: #f1f5f9; }
            .data-table .tc { text-align: center; }
            .data-table .tr { text-align: right; }
            .data-table .bg-blue { background: #eff6ff; }
            .data-table .bg-orange { background: #fff7ed; }
            .data-table .bg-orange-bold { background: #ffedd5; }
            .data-table .vol-header { background: #dbeafe; }
            .data-table .energy-header { background: #ffedd5; }
            .data-table .total-row { background: #f1f5f9; font-weight: bold; }

            .page-break { page-break-before: always; }
        `;

        return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head><meta charset="utf-8">
        <style>
            @page { size: A4 landscape; margin: 1cm; }
            ${excelStyles}
        </style>
        </head><body>${body}</body></html>`;
    };

    // ==========================================
    // Скачивание файла
    // ==========================================
    const downloadFile = (content, filename, mimeType) => {
        const blob = new Blob(['\ufeff', content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const exportToExcel = () => {
        if (!reportData) return;
        downloadFile(
            buildReportHtml(false),
            `Теплоэнергия_${lastParams.dateFrom}_${lastParams.dateTo}.xls`,
            'application/vnd.ms-excel'
        );
    };

    const exportToWord = () => {
        if (!reportData) return;
        downloadFile(
            buildReportHtml(true),
            `Теплоэнергия_${lastParams.dateFrom}_${lastParams.dateTo}.doc`,
            'application/msword'
        );
    };

    return (
        <div className="p-4 sm:p-6 space-y-4 max-w-[95rem] mx-auto min-h-screen">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <button onClick={() => navigate('/reports')} className="flex items-center gap-2 text-slate-400 hover:text-white transition text-sm font-medium">
                    <ArrowLeft className="w-4 h-4" /> Назад к отчетным формам
                </button>
                {reportData && (
                    <div className="flex gap-2">
                        <button onClick={() => setIsModalOpen(true)} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-medium transition flex items-center gap-2">
                            <Settings className="w-4 h-4" /> Изменить параметры
                        </button>
                        <button onClick={exportToWord} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition flex items-center gap-2">
                            <FileText className="w-4 h-4" /> Скачать Word
                        </button>
                        <button onClick={exportToExcel} className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-medium transition flex items-center gap-2">
                            <Download className="w-4 h-4" /> Скачать Excel
                        </button>
                    </div>
                )}
            </div>

            {!reportData ? (
                <div className="flex flex-col items-center justify-center py-20 bg-slate-800/50 rounded-2xl border border-slate-700 border-dashed">
                    <Thermometer className="w-16 h-16 text-slate-600 mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2">Отчет не сформирован</h3>
                    <p className="text-slate-400 text-sm mb-6 text-center max-w-md">
                        Выберите адреса, устройства и период для расчета фактического потребления тепловой энергии.
                    </p>
                    <button onClick={() => setIsModalOpen(true)} className="px-6 py-3 bg-orange-600 hover:bg-orange-500 text-white rounded-xl text-sm font-medium transition flex items-center gap-2 shadow-lg shadow-orange-900/20">
                        <Settings className="w-5 h-5" /> Настроить и сформировать отчет
                    </button>
                </div>
            ) : (
                <>
                    {/* Шапка организации и согласований */}
                    <div
                        className="bg-white text-slate-900 shadow-lg mx-auto"
                        style={{
                            fontFamily: "'Times New Roman', Times, serif",
                            maxWidth: '21cm',
                            minWidth: '21cm',
                            padding: '0.5cm 0.5cm',
                            fontSize: '11pt',
                            lineHeight: '1.3'
                        }}
                    >
                        <div className="text-center mb-5" style={{ fontWeight: 'bold', fontSize: '16pt' }}>
                            <p>Общество с ограниченной ответственностью</p>
                            <p>«Управляющая компания Златоград»</p>
                            <p>(ООО «УК Златоград»)</p>
                            <p className="mt-2 text-slate-700" style={{ fontWeight: 'normal', fontSize: '12pt' }}>
                                Россия, 456209, г. Златоуст, ул. Шоссейная, 1, помещение № 17
                            </p>
                            <p className="text-slate-700" style={{ fontWeight: 'normal', fontSize: '12pt' }}>
                                ИНН 7404074032 КПП 740401001
                            </p>
                            <p className="mt-2">______________________________________________________________________</p>
                        </div>

                        <div className="grid grid-cols-3 gap-9 mt-0 mb-4" style={{ fontSize: '12pt', lineHeight: '1.3' }}>
                            <div className="text-left">
                                <p className="font-bold">Согласовано</p>
                                <p>Директор</p>
                                <p>ООО «НПО Автоматика-Сервис»</p>
                                <p className="mt-2">_______________Кайсарова Т. В.</p>
                                <p className="mt-1">«&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;» _______________ 202_ г.</p>
                            </div>
                            <div className="text-left">
                                <p>Согласовано</p>
                                <p>Главный энергетик-начальник</p>
                                <p>отдела № 263 АО «Златмаш»</p>
                                <p className="mt-2">______________Тюрин С. А.</p>
                                <p className="mt-1">«&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;» ____________ 202_ г.</p>
                            </div>
                            <div className="text-left">
                                <p>Согласовано</p>
                                <p>Директор</p>
                                <p>ООО УК «Златоград»</p>
                                <p className="mt-2">__________ Сергеев В. Ю.</p>
                                <p className="mt-1">«&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;» ____________ 202_ г.</p>
                            </div>
                        </div>

                        <div className="mt-4">
                            <h1 className="text-center font-bold" style={{ fontSize: '12pt', marginBottom: '1pt' }}>
                                Количество потребленной тепловой энергии
                            </h1>
                            <h1 className="text-center font-bold" style={{ fontSize: '12pt', marginBottom: '2pt' }}>
                                за период {reportData.periodStringFull}
                            </h1>

                            <p className="text-center" style={{ fontSize: '10pt', marginBottom: '10pt' }}>
                                Отчет от {reportData.dateOfReport}
                            </p>

                            <div style={{ fontSize: '11pt', lineHeight: '1.3', margin: '8pt 0 12pt 0' }}>
                                <p><span className="font-bold">Поставщик ТЭ:</span> АО "Златмаш"</p>
                                <p className="font-bold" style={{ textIndent: '0cm', marginTop: '4pt' }}>
                                    Формула расчета фактического количества ТЭ жилых и нежилых помещений, Гкал:
                                </p>
                                <p className="font-bold" style={{ textIndent: '0cm' }}>
                                    Qобщ факт = Qтп + Qкорр. + Qиз,
                                </p>
                                <p style={{ paddingLeft: '0' }}>
                                    где Qтп - количество ТЭ, израсходованной на компенсацию потерь, Гкал,
                                </p>
                                <p style={{ paddingLeft: '1.6em' }}>
                                    Qкорр. - количество ТЭ, израсходованной потребителем за время действия НС, Гкал,
                                </p>
                                <p style={{ paddingLeft: '1.6em' }}>
                                    Qиз - рассчитанное теплосчетчиком в штатном режиме количество ТЭ, Гкал.
                                </p>
                                <p className="font-bold" style={{ textIndent: '0cm', marginTop: '4pt' }}>
                                    Формула расчета количества ТЭ, израсходованного потребителем за время действия НС, Гкал:
                                </p>
                                <p className="font-bold" style={{ textIndent: '0cm' }}>
                                    Qкорр. = f((Qиз / ВН) * ВП, Вост),
                                </p>
                                <p>где ВН - время наработки, ч,</p>
                                <p style={{ paddingLeft: '1.6em' }}>ВП - время простоя, ч,</p>
                                <p style={{ paddingLeft: '1.6em' }}>Вост - время отключения ТЭЦ (план), ч.</p>
                            </div>
                        </div>
                    </div>

                    {/* Блоки по домам */}
                    {reportData.houses.map((house, idx) => (
                        <div
                            key={idx}
                            className="bg-white text-slate-900 shadow-lg mx-auto"
                            style={{
                                fontFamily: "'Times New Roman', Times, serif",
                                maxWidth: '21cm',
                                minWidth: '21cm',
                                padding: '0.5cm 1.2cm 1cm 1.2cm',
                                fontSize: '11pt'
                            }}
                        >
                            <HouseReportBlock
                                house={house}
                                reportData={reportData}
                                pageIndex={idx + 1}
                            />
                        </div>
                    ))}
                </>
            )}

            <EnergyConfigModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onGenerate={handleGenerateReport}
            />
        </div>
    );
};

export default EnergyConsumptionReportPage;